/**
 * Trip CO2 Calculator — share button + share dialog.
 *
 * The trigger button (primary / compact variants) opens a native `<dialog>`
 * offering a *menu of share methods* rather than auto-picking one: different
 * audiences want different channels, and the editable message lets people
 * tweak the wording before sending.
 *
 * The message + channel grid itself is the shared `ShareChannels` block
 * (`src/shared/share/ShareChannels.tsx`), extracted for trip sharing v1 so
 * the calculator and the trip share dialog stay one implementation. This file
 * keeps the calculator-specific parts: the teal trigger buttons and the six
 * calculator-named Mixpanel events (dialog open + one per channel — the open
 * count minus the channel counts is the dialog-abandonment rate).
 */

import React, { useEffect, useRef } from 'react';
import { analytics } from '../../shared/analytics';
import ShareChannels, { ShareChannel } from '../../shared/share/ShareChannels';
import ScrollMoreHint from '../../components/ui/ScrollMoreHint';

export interface ShareButtonProps {
  /** Fully-built share URL (output of `buildShareUrl(trip)`). */
  shareUrl: string;
  /** Pre-formatted share-payload text (output of `formatShareText(...)`). */
  shareText: string;
  /** Visual variant for the trigger button. */
  variant?: 'primary' | 'compact';
  /**
   * Extra event properties attached to every share-family Mixpanel
   * event (button click + each per-method event), so the analyst can
   * slice shares by mode / route / placement without losing the
   * channel-identity carried by the event name itself.
   */
  trackingProps?: Record<string, unknown>;
}

const ACCENT = 'hsl(174, 70%, 43%)'; // calculator teal (distinct from My Trips green)

const ShareButton: React.FC<ShareButtonProps> = ({
  shareUrl,
  shareText,
  variant = 'primary',
  trackingProps,
}) => {
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  // Close-on-backdrop-click (same pattern as CantTrainDialog).
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const onClick = (e: MouseEvent) => {
      if (e.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      const inside =
        e.clientX >= rect.left && e.clientX <= rect.right &&
        e.clientY >= rect.top && e.clientY <= rect.bottom;
      if (!inside) dialog.close();
    };
    dialog.addEventListener('click', onClick);
    return () => dialog.removeEventListener('click', onClick);
  }, []);

  const openDialog = () => {
    dialogRef.current?.showModal();
    // "Share intent" event on open; channel events follow on pick.
    analytics.trackTravelImpactCalculatorShareButtonClick(trackingProps ?? {});
  };
  const closeDialog = () => dialogRef.current?.close();

  const handleChannel = (channel: ShareChannel) => {
    const props = trackingProps ?? {};
    if (channel === 'copy_link') analytics.trackTravelImpactCalculatorShareLinkClipboard(props);
    else if (channel === 'copy') analytics.trackTravelImpactCalculatorShareClipboard(props);
    else if (channel === 'email') analytics.trackTravelImpactCalculatorShareEmail(props);
    else if (channel === 'whatsapp') analytics.trackTravelImpactCalculatorShareWhatsApp(props);
    else if (channel === 'x') analytics.trackTravelImpactCalculatorShareX(props);
    else if (channel === 'web_share') analytics.trackTravelImpactCalculatorShareWebShare(props);
  };

  return (
    <>
      {variant === 'compact' ? (
        <CompactTriggerButton onClick={openDialog} />
      ) : (
        <PrimaryTriggerButton onClick={openDialog} />
      )}

      <dialog
        ref={dialogRef}
        aria-labelledby="share-dialog-title"
        className="scroll-visible"
        style={{
          padding: 0,
          border: 0,
          borderRadius: 14,
          width: 'min(460px, 92vw)',
          background: '#fff',
          color: '#0f172a',
          boxShadow: '0 24px 56px rgba(15, 23, 42, 0.25)',
        }}
      >
        <div style={{ padding: '1.25rem 1.25rem 1.1rem', position: 'relative' }}>
          <button
            type="button"
            onClick={closeDialog}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              padding: '4px 8px',
              fontSize: 16,
              lineHeight: 1,
              background: 'transparent',
              border: 0,
              color: '#64748b',
              cursor: 'pointer',
            }}
          >
            ×
          </button>

          <h3
            id="share-dialog-title"
            style={{ fontSize: 18, margin: '0 0 0.25rem', letterSpacing: -0.2, lineHeight: 1.2 }}
          >
            Share these results
          </h3>
          <p style={{ margin: '0 0 0.75rem', fontSize: 13, color: '#475569', lineHeight: 1.45 }}>
            Edit the message below if you like, then pick how to share it.
          </p>

          <ShareChannels
            shareText={`${shareText} ${shareUrl}`}
            shareUrl={shareUrl}
            emailSubject="Travel Impact Calculator"
            shareTitle="Travel Impact Calculator"
            accentColor={ACCENT}
            onChannel={handleChannel}
            onDone={closeDialog}
            copyToastMessage="Paste it anywhere to share this travel."
          />
          {/* The modal <dialog> is the scroll container (browser default). */}
          <ScrollMoreHint scrollRef={dialogRef} />
        </div>
      </dialog>
    </>
  );
};

export default ShareButton;

// ─────────────────────────────────────────────────────────────────────────────
// Trigger button variants
// ─────────────────────────────────────────────────────────────────────────────

const shareGlyph = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 3v12M12 3l-4 4M12 3l4 4M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PrimaryTriggerButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '0.7rem 1.1rem',
      fontSize: 14,
      fontWeight: 700,
      color: '#fff',
      background: ACCENT,
      border: 0,
      borderRadius: 999,
      cursor: 'pointer',
      boxShadow: '0 6px 14px rgba(13, 148, 136, 0.28)',
      transition: 'transform 0.12s ease, box-shadow 0.12s ease',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-1px)';
      e.currentTarget.style.boxShadow = '0 8px 20px rgba(13, 148, 136, 0.34)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = '';
      e.currentTarget.style.boxShadow = '0 6px 14px rgba(13, 148, 136, 0.28)';
    }}
  >
    {shareGlyph}
    <span>Share these results</span>
  </button>
);

const CompactTriggerButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '8px 14px',
      fontSize: 13,
      fontWeight: 600,
      color: '#0f172a',
      background: 'transparent',
      border: '1px solid #cbd5e1',
      borderRadius: 999,
      cursor: 'pointer',
    }}
  >
    {shareGlyph}
    <span>Share these results</span>
  </button>
);
